import { createContext, useContext } from 'react'
import { create, type StoreApi, type UseBoundStore } from 'zustand'
import {
    CandidateLiteDto,
    InterestedValues,
    PresentCandidateValues,
} from '../../../api/role/models/CandidateDto'
import roleApi from '../../../api/role/RoleApi'
import { UserDto } from '../../../api/tenant/models/UserDto'
import { InterviewForThisRoleValues } from '../../../api/role/models/CandidateConnectionDto'
import { PersonLiteDto } from '../../../api/person/models/PersonDto'
import { MapExtra } from '../../../utils/MapExtra'
import personApi from '../../../api/person/PersonApi'
import { RoleLiteDto } from '../../../api/role/models/RoleDto'
import { CandidateFeedbackData } from '../../../api/role/models/saveHiringManagerFeedback/SaveHiringManagerFeedbackReq'
import { FeedbackValues } from '../../../api/role/models/FeedbackDto'
import mitt, { Emitter } from 'mitt'

// Define the events your emitter will handle
type CandidateEvents = {
    candidateChanged: CandidateLiteDto
}

// Create a typed emitter for candidate events
const emitter: Emitter<CandidateEvents> = mitt<CandidateEvents>()

type CandidateContainer = 'candidates' | 'ephemeralCandidates'

type PresentUpdateTracker = {
    chain: Promise<void>
    lastPersisted?: PresentCandidateValues
    lastPersistedContainer?: CandidateContainer
    pendingCount: number
}

export const ManageCandidateStoreContext = createContext<UseBoundStore<
    StoreApi<ManageCandidateStoreState>
> | null>(null)

type UseStoreWithEquality = {
    <U>(
        selector: (state: ManageCandidateStoreState) => U,
        equalityFn?: (a: U, b: U) => boolean,
    ): U
    (): UseBoundStore<StoreApi<ManageCandidateStoreState>>
}

// Overload signatures
export function useManageCandidateStore<T>(
    selector: (state: ManageCandidateStoreState) => T,
    equalityFn?: (a: T, b: T) => boolean,
): T
export function useManageCandidateStore(): UseBoundStore<
    StoreApi<ManageCandidateStoreState>
>

// Implementation
export function useManageCandidateStore<T>(
    selector?: (state: ManageCandidateStoreState) => T,
    equalityFn?: (a: T, b: T) => boolean,
) {
    const store = useContext(ManageCandidateStoreContext)
    if (!store) {
        throw new Error(
            'useManageCandidateStore must be used within a ManageCandidateProvider',
        )
    }
    // Cast the store to a type that accepts the equality function.
    const typedStore = store as unknown as UseStoreWithEquality
    return selector ? typedStore(selector, equalityFn) : store
}

export interface ManageCandidateStoreState {
    personLiteCache: MapExtra<string, PersonLiteDto>
    candidates: CandidateLiteDto[]
    ephemeralCandidates: CandidateLiteDto[]
    roleLite?: RoleLiteDto
    user?: UserDto
    selectedCandidateId?: string
    presentLoadingByCandidateId: MapExtra<string, boolean>
    feedbackLoadingByCandidateId: MapExtra<string, boolean>
    fetchingCandidates: boolean
    setFetchingCandidates: (fetching: boolean) => void
    setUser: (user: UserDto) => void
    setRoleLite: (roleLite: RoleLiteDto) => void
    setCandidates: (candidates: CandidateLiteDto[]) => void
    setEphemeralCandidates: (candidates: CandidateLiteDto[]) => void
    clearCandidates: () => void
    primePersonCache(personIds: string[]): Promise<void>
    getUserSafe(): UserDto
    safeGetRoleLite(): RoleLiteDto
    selectCandidateId(candidateId: string): void
    updatePresentForCandidate(
        candidateId: string,
        value?: PresentCandidateValues,
    ): Promise<void>
    updateContactedForCandidate(
        candidateId: string,
        contacted: boolean,
        localOnly?: boolean,
    ): Promise<void>
    updateInterestedForCandidate(
        candidateId: string,
        value?: InterestedValues,
    ): Promise<void>
    updateInterviewForCandidate(
        candidateId: string,
        value?: InterviewForThisRoleValues,
    ): Promise<void>
    updateFeedbackForCandidate(
        candidateFeedbackData: CandidateFeedbackData,
    ): Promise<void>
    localUpdateFeedbackComment(
        candidateId: string,
        feedbackComment: string,
    ): void
    updateFeedbackValueForCandidate(
        candidateId: string,
        value: FeedbackValues,
    ): Promise<void>
    updateFeedbackCommentForCandidate(candidateId: string): Promise<void>
    onCandidateChanged: (
        callback: (updatedCandidate: CandidateLiteDto) => void,
    ) => () => void
}

const getPresentValueFromState = (
    state: ManageCandidateStoreState,
    personId: string,
): PresentCandidateValues | undefined => {
    const candidate =
        state.candidates.find(
            (candidate) => candidate.candidatePersonId === personId,
        ) ??
        state.ephemeralCandidates.find(
            (candidate) => candidate.candidatePersonId === personId,
        )

    return candidate?.present
}

const getCandidateContainer = (
    state: ManageCandidateStoreState,
    personId: string,
): CandidateContainer | undefined => {
    if (
        state.candidates.some(
            (candidate) => candidate.candidatePersonId === personId,
        )
    ) {
        return 'candidates'
    }

    if (
        state.ephemeralCandidates.some(
            (candidate) => candidate.candidatePersonId === personId,
        )
    ) {
        return 'ephemeralCandidates'
    }

    return undefined
}

const applyPresentUpdate = (
    state: ManageCandidateStoreState,
    personId: string,
    value?: PresentCandidateValues,
    targetContainer: CandidateContainer = 'candidates',
) => {
    let updatedCandidate: CandidateLiteDto | undefined

    if (targetContainer === 'candidates') {
        const updatedCandidates = state.candidates.map((candidate) => {
            if (candidate.candidatePersonId !== personId) {
                return candidate
            }

            updatedCandidate = {
                ...candidate,
                present: value,
            }
            return updatedCandidate
        })

        const remainingEphemeralCandidates = state.ephemeralCandidates.filter(
            (candidate) => candidate.candidatePersonId !== personId,
        )

        if (!updatedCandidate) {
            const ephemeralCandidate = state.ephemeralCandidates.find(
                (candidate) => candidate.candidatePersonId === personId,
            )

            if (ephemeralCandidate) {
                updatedCandidate = {
                    ...ephemeralCandidate,
                    present: value,
                }
                updatedCandidates.push(updatedCandidate)
            } else {
                updatedCandidate = {
                    candidatePersonId: personId,
                    present: value,
                    contacted: false,
                    connections: [],
                    companyConnections: [],
                    rankingScore: 0,
                    closenessScore: 0,
                    jobRelevanceScore: 0,
                    jobRelevanceRawScore: 0,
                    jobRelevanceJobTitle: '',
                }

                updatedCandidates.push(updatedCandidate)
            }
        }

        return {
            candidates: updatedCandidates,
            ephemeralCandidates: remainingEphemeralCandidates,
            updatedCandidate,
        }
    }

    // target container is ephemeralCandidates
    const remainingCandidates = state.candidates.filter(
        (candidate) => candidate.candidatePersonId !== personId,
    )

    const updatedEphemeral = state.ephemeralCandidates.map((candidate) => {
        if (candidate.candidatePersonId !== personId) {
            return candidate
        }

        updatedCandidate = {
            ...candidate,
            present: value,
        }

        return updatedCandidate
    })

    if (!updatedCandidate) {
        updatedCandidate = {
            candidatePersonId: personId,
            present: value,
            contacted: false,
            connections: [],
            companyConnections: [],
            rankingScore: 0,
            closenessScore: 0,
            jobRelevanceScore: 0,
            jobRelevanceRawScore: 0,
            jobRelevanceJobTitle: '',
        }

        updatedEphemeral.push(updatedCandidate)
    }

    return {
        candidates: remainingCandidates,
        ephemeralCandidates: updatedEphemeral,
        updatedCandidate,
    }
}

export const createManageCandidateStore = () => {
    const presentUpdateTrackers = new Map<string, PresentUpdateTracker>()

    return create<ManageCandidateStoreState>((set, get) => ({
        personLiteCache: new MapExtra<string, PersonLiteDto>(),
        candidates: [],
        ephemeralCandidates: [],
        presentLoadingByCandidateId: new MapExtra<string, boolean>(),
        feedbackLoadingByCandidateId: new MapExtra<string, boolean>(),
        fetchingCandidates: false,
        setFetchingCandidates: (fetching) =>
            set({ fetchingCandidates: fetching }),
        localUpdateFeedbackComment: (candidateId, feedbackComment) => {
            const state = get()
            const hmPersonId = state.getUserSafe().personId!

            const updatedCandidates = state.candidates.map((candidate) => {
                if (candidate.candidatePersonId !== candidateId)
                    return candidate

                return {
                    ...candidate,
                    connections: candidate.connections.map((connection) => {
                        if (connection.connectedPersonId !== hmPersonId)
                            return connection

                        return {
                            ...connection,
                            feedback: {
                                ...connection.feedback,
                                comment: feedbackComment,
                                value:
                                    connection.feedback?.value ??
                                    FeedbackValues.HIDDEN,
                            },
                        }
                    }),
                }
            })

            set({ candidates: updatedCandidates })
        },
        updateInterviewForCandidate: async (
            candidateId: string,
            value?: InterviewForThisRoleValues,
        ) => {
            const state = get()
            const hmPersonId = state.getUserSafe().personId!

            const candidate = state.candidates.find(
                (candidate) => candidate.candidatePersonId === candidateId,
            )
            if (!candidate) {
                throw new Error(
                    `updateInterviewForCandidate: Candidate not found for id: ${candidateId}`,
                )
            }

            const candidateConn = candidate.connections.find(
                (connection) => connection.connectedPersonId === hmPersonId,
            )
            if (!candidateConn) {
                throw new Error(
                    `updateInterviewForCandidate: Connection not found for candidateId: ${candidateId} and hmId: ${hmPersonId}`,
                )
            }

            await state.updateFeedbackForCandidate({
                candidateId,
                feedback: {
                    value:
                        candidateConn.feedback?.value ?? FeedbackValues.HIDDEN,
                    comment: candidateConn.feedback?.comment,
                },
                interviewForThisRole: value,
            })

            // Update the in-memory candidate with the new feedback value
            set((state) => ({
                candidates: state.candidates.map((candidate) =>
                    candidate.candidatePersonId === candidateId
                        ? {
                              ...candidate,
                              connections: candidate.connections.map(
                                  (connection) =>
                                      connection.connectedPersonId ===
                                      hmPersonId
                                          ? {
                                                ...connection,
                                                interviewForThisRole: value,
                                            }
                                          : connection,
                              ),
                          }
                        : candidate,
                ),
            }))
        },
        updateFeedbackCommentForCandidate: async (candidateId) => {
            // the comment has already been changed in the local state

            const state = get()
            const hmPersonId = state.getUserSafe().personId!

            const candidate = state.candidates.find(
                (candidate) => candidate.candidatePersonId === candidateId,
            )
            if (!candidate) {
                throw new Error(
                    `updateFeedbackCommentForCandidate: Candidate not found for id: ${candidateId}`,
                )
            }

            const candidateConn = candidate.connections.find(
                (connection) => connection.connectedPersonId === hmPersonId,
            )
            if (!candidateConn) {
                throw new Error(
                    `updateFeedbackCommentForCandidate: Connection not found for candidateId: ${candidateId} and hmId: ${hmPersonId}`,
                )
            }

            await state.updateFeedbackForCandidate({
                candidateId,
                feedback: {
                    value:
                        candidateConn.feedback?.value ?? FeedbackValues.HIDDEN,
                    comment: candidateConn.feedback?.comment,
                },
                interviewForThisRole: candidateConn.interviewForThisRole,
            })
        },
        updateFeedbackValueForCandidate: async (candidateId, value) => {
            const state = get()
            const hmPersonId = state.getUserSafe().personId!

            const candidate = state.candidates.find(
                (candidate) => candidate.candidatePersonId === candidateId,
            )
            if (!candidate) {
                throw new Error(
                    `updateFeedbackValueForCandidate: Candidate not found for id: ${candidateId}`,
                )
            }

            const candidateConn = candidate.connections.find(
                (connection) => connection.connectedPersonId === hmPersonId,
            )
            if (!candidateConn) {
                throw new Error(
                    `updateFeedbackValueForCandidate: Connection not found for candidateId: ${candidateId} and hmId: ${hmPersonId}`,
                )
            }

            await state.updateFeedbackForCandidate({
                candidateId,
                feedback: {
                    value,
                    comment: candidateConn.feedback?.comment,
                },
                interviewForThisRole: candidateConn.interviewForThisRole,
            })

            // Update the in-memory candidate with the new feedback value
            set((state) => ({
                candidates: state.candidates.map((candidate) =>
                    candidate.candidatePersonId === candidateId
                        ? {
                              ...candidate,
                              connections: candidate.connections.map(
                                  (connection) =>
                                      connection.connectedPersonId ===
                                      hmPersonId
                                          ? {
                                                ...connection,
                                                feedback: {
                                                    ...connection.feedback,
                                                    value, // Updated feedback value
                                                },
                                            }
                                          : connection,
                              ),
                          }
                        : candidate,
                ),
            }))
        },

        updateFeedbackForCandidate: async (
            candidateFeedbackData: CandidateFeedbackData,
        ) => {
            const state = get()

            set((state) => {
                const newMap = new MapExtra<string, boolean>(
                    state.feedbackLoadingByCandidateId.entries(),
                )
                newMap.set(candidateFeedbackData.candidateId, true)
                return { feedbackLoadingByCandidateId: newMap }
            })

            try {
                await roleApi.saveHiringManagerFeedbackForCandidate({
                    roleId: state.safeGetRoleLite().id,
                    hiringManagerId: state.getUserSafe().personId!,
                    candidateFeedbackData,
                })
            } finally {
                set((state) => {
                    const newMap = new MapExtra<string, boolean>(
                        state.feedbackLoadingByCandidateId.entries(),
                    )
                    newMap.set(candidateFeedbackData.candidateId, false)
                    return { feedbackLoadingByCandidateId: newMap }
                })
            }
        },
        onCandidateChanged: (
            callback: (updatedCandidate: CandidateLiteDto) => void,
        ): (() => void) => {
            emitter.on('candidateChanged', callback)
            return () => emitter.off('candidateChanged', callback)
        },
        updatePresentForCandidate: async (
            personId: string,
            value?: PresentCandidateValues,
        ) => {
            const tracker =
                presentUpdateTrackers.get(personId) ??
                (() => {
                    const stateSnapshot = get()
                    const newTracker: PresentUpdateTracker = {
                        chain: Promise.resolve(),
                        lastPersisted: getPresentValueFromState(
                            stateSnapshot,
                            personId,
                        ),
                        lastPersistedContainer:
                            getCandidateContainer(stateSnapshot, personId) ??
                            'candidates',
                        pendingCount: 0,
                    }
                    presentUpdateTrackers.set(personId, newTracker)
                    return newTracker
                })()

            tracker.pendingCount += 1

            set((state) => {
                const { candidates, ephemeralCandidates, updatedCandidate } =
                    applyPresentUpdate(state, personId, value)

                if (updatedCandidate) {
                    emitter.emit('candidateChanged', updatedCandidate)
                }

                return {
                    candidates,
                    ephemeralCandidates,
                }
            })

            const run = async () => {
                set((state) => {
                    const newMap = new MapExtra<string, boolean>(
                        state.presentLoadingByCandidateId.entries(),
                    )
                    newMap.set(personId, true)
                    return { presentLoadingByCandidateId: newMap }
                })

                try {
                    await roleApi.setCandidatesPresentValue({
                        candidateIds: [personId],
                        roleId: get().safeGetRoleLite().id,
                        presentCandidateValue: value,
                    })

                    tracker.lastPersisted = value
                    tracker.lastPersistedContainer = 'candidates'
                } catch (error) {
                    const pendingAfterThis = Math.max(
                        0,
                        tracker.pendingCount - 1,
                    )

                    if (pendingAfterThis === 0) {
                        set((state) => {
                            const {
                                candidates,
                                ephemeralCandidates,
                                updatedCandidate,
                            } = applyPresentUpdate(
                                state,
                                personId,
                                tracker.lastPersisted,
                                tracker.lastPersistedContainer,
                            )

                            if (updatedCandidate) {
                                emitter.emit(
                                    'candidateChanged',
                                    updatedCandidate,
                                )
                            }

                            return {
                                candidates,
                                ephemeralCandidates,
                            }
                        })
                    }

                    throw error
                } finally {
                    tracker.pendingCount = Math.max(0, tracker.pendingCount - 1)

                    set((state) => {
                        const newMap = new MapExtra<string, boolean>(
                            state.presentLoadingByCandidateId.entries(),
                        )
                        newMap.set(personId, false)
                        return { presentLoadingByCandidateId: newMap }
                    })
                }
            }

            tracker.chain = tracker.chain.catch(() => undefined).then(run)

            return tracker.chain
        },
        updateContactedForCandidate: async (
            personId: string,
            contacted: boolean,
            localOnly: boolean = false,
        ) => {
            const stateVar = get()

            if (!localOnly) {
                // TODO: we need to have the server respond with the date that it used for the
                //  interestedDate field
                await roleApi.setCandidateContactedValue({
                    candidateId: personId,
                    roleId: stateVar.safeGetRoleLite().id,
                    contacted,
                })
            }

            set((state) => ({
                candidates: state.candidates.map((candidate) => {
                    if (candidate.candidatePersonId === personId) {
                        const updatedCandidate = {
                            ...candidate,
                            contacted,
                        }
                        // Emit the event with the updated candidate as payload.
                        emitter.emit('candidateChanged', updatedCandidate)
                        return updatedCandidate
                    }
                    return candidate
                }),
            }))
        },
        updateInterestedForCandidate: async (
            personId: string,
            value?: InterestedValues,
        ) => {
            const stateVar = get()

            // TODO: we need to have the server respond with the date that it used for the
            //  interestedDate field
            await roleApi.setCandidateInterestedValue({
                candidateId: personId,
                roleId: stateVar.safeGetRoleLite().id,
                interested: value,
            })

            set((state) => ({
                candidates: state.candidates.map((candidate) => {
                    if (candidate.candidatePersonId === personId) {
                        const updatedCandidate = {
                            ...candidate,
                            interested: value,
                        }
                        // Emit the event with the updated candidate as payload.
                        emitter.emit('candidateChanged', updatedCandidate)
                        return updatedCandidate
                    }
                    return candidate
                }),
            }))
        },
        setUser: (user) => set({ user }),
        setRoleLite: (roleLite) => set({ roleLite }),
        selectCandidateId: (candidateId) =>
            set({ selectedCandidateId: candidateId }),
        getUserSafe: (): UserDto => {
            const state = get()
            if (!state.user) {
                throw new Error('User not set in ManageCandidateStore')
            }
            return state.user
        },
        safeGetRoleLite: (): RoleLiteDto => {
            const state = get()
            if (!state.roleLite) {
                throw new Error('RoleLite not set in ManageCandidateStore')
            }
            return state.roleLite
        },
        setCandidates: (candidates) => set({ candidates }),
        setEphemeralCandidates: (candidates: CandidateLiteDto[]) =>
            set({ ephemeralCandidates: candidates }),
        clearCandidates: () => set({ candidates: [] }),
        primePersonCache: async (personIds: string[]): Promise<void> => {
            const state = get()

            // ensure we don't prime the cache with ids that are already in the cache
            const idsToPrime = personIds.filter(
                (id) => !state.personLiteCache.has(id),
            )

            if (idsToPrime.length === 0) {
                return
            }

            const persons = (
                await personApi.getPersonsLite({
                    personIds: idsToPrime,
                })
            ).persons

            for (const person of persons) {
                state.personLiteCache.set(person.id, person)
            }
        },
    }))
}
